import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Edit, ChevronDown } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

const SalesForm = ({ isOpen, onClose, onSave, operators, flags, paymentMethods, sale, receipts }) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({});
  const [availableFlags, setAvailableFlags] = useState([]);
  // Estados de histórico de pagamentos removidos - modelo 1:1 não necessita histórico

  const formatValueForInput = (value) => {
    if (value === null || value === undefined) return '';
    const numberValue = Number(value);
    if (isNaN(numberValue)) return '';
    return numberValue.toFixed(2).replace('.', ',');
  }

  const initializeFormData = (saleData) => {
    return {
      data: saleData?.data ? new Date(saleData.data.replace(/-/g, '/')).toISOString().split('T')[0] : '',
      dataPgt: saleData?.datapgt ? new Date(saleData.datapgt.replace(/-/g, '/')).toISOString().split('T')[0] : '',
      bandeira: saleData?.bandeira || '',
      parcelas: saleData?.parcelas?.toString() || '1',
      tipoTransacao: saleData?.tipotransacao || '',
      valorCompra: formatValueForInput(saleData?.valorcompra),
      codigoAutorizacao: saleData?.codigoautorizacao || '',
      codigoPedido: saleData?.codigopedido || '',
      valorRecebido: formatValueForInput(saleData?.valorrecebido),
      status: saleData?.status || 'aberto',
      empresa: saleData?.empresa || ''
    };
  };

  useEffect(() => {
    if (isOpen && sale) {
      setFormData(initializeFormData(sale));
    }
  }, [sale, isOpen]);

  // useEffect de histórico removido - não mais necessário

  // Função fetchPaymentHistory removida - modelo 1:1 não necessita histórico

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleOperatorChange = (value) => {
    handleInputChange('empresa', value);
    handleInputChange('bandeira', '');
  };

  useEffect(() => {
    if (!isOpen || !formData.empresa) {
      setAvailableFlags(flags);
      return;
    }

    const selectedOperator = operators.find(op => op.name === formData.empresa);
    const linkedFlagIds = selectedOperator?.flagIds || [];

    // Separar bandeiras vinculadas das não vinculadas
    const linkedFlags = flags.filter(flag => linkedFlagIds.includes(flag.id));
    const otherFlags = flags.filter(flag => !linkedFlagIds.includes(flag.id));

    // Unir as duas listas colocando as vinculadas no topo
    setAvailableFlags([...linkedFlags, ...otherFlags]);

  }, [formData.empresa, operators, flags, isOpen]);


  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.data || !formData.bandeira || !formData.valorCompra || !formData.empresa || !formData.tipoTransacao) {
      toast({ title: "Erro", description: "Por favor, preencha todos os campos obrigatórios (*).", variant: "destructive" });
      return;
    }

    onSave(formData);
  };


  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl"><Edit className="w-5 h-5" /> {sale ? 'Editar Venda' : 'Lançar Venda'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="data">Data da Venda *</Label>
              <Input id="data" type="date" value={formData.data || ''} onChange={(e) => handleInputChange('data', e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dataPgt">Data do Pagamento</Label>
              <Input
                id="dataPgt"
                type="date"
                value={formData.dataPgt || ''}
                onChange={(e) => handleInputChange('dataPgt', e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="empresa">Operadora *</Label>
              <Select value={formData.empresa || ''} onValueChange={handleOperatorChange}>
                <SelectTrigger><SelectValue placeholder="Selecione a operadora" /></SelectTrigger>
                <SelectContent>
                  {operators.map((op) => (
                    <SelectItem key={op.id} value={op.name}>{op.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="bandeira">Bandeira *</Label>
              <Select value={formData.bandeira || ''} onValueChange={(value) => handleInputChange('bandeira', value)} disabled={!formData.empresa}>
                <SelectTrigger><SelectValue placeholder="Selecione a bandeira" /></SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {availableFlags.map((flag) => (
                    <SelectItem key={flag.id} value={flag.name}>{flag.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="tipoTransacao">Tipo *</Label>
              <Select value={formData.tipoTransacao || ''} onValueChange={(value) => handleInputChange('tipoTransacao', value)}>
                <SelectTrigger><SelectValue placeholder="Selecione o tipo" /></SelectTrigger>
                <SelectContent>
                  {paymentMethods.map(pm => <SelectItem key={pm.id} value={pm.name}>{pm.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="parcelas">Parcelas</Label>
              <Select value={formData.parcelas || '1'} onValueChange={(value) => handleInputChange('parcelas', value)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[...Array(12)].map((_, i) => (
                    <SelectItem key={i + 1} value={(i + 1).toString()}>{i + 1}x</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={formData.status || 'aberto'} onValueChange={(value) => handleInputChange('status', value)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Em Aberto">Em Aberto</SelectItem>
                  <SelectItem value="Quitado">Quitado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="valorCompra">Valor da Compra (R$) *</Label>
              <Input id="valorCompra" type="text" placeholder="0,00" value={formData.valorCompra || ''} onChange={(e) => handleInputChange('valorCompra', e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="valorRecebido">Valor Recebido (R$)</Label>
              <Input id="valorRecebido" type="text" placeholder="0,00" value={formData.valorRecebido || ''} onChange={(e) => handleInputChange('valorRecebido', e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="codigoAutorizacao">Código de Autorização</Label>
              <Input id="codigoAutorizacao" placeholder="Digite o código de autorização" value={formData.codigoAutorizacao || ''} onChange={(e) => handleInputChange('codigoAutorizacao', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="codigoPedido">Cód. de Pedido</Label>
              <Input id="codigoPedido" placeholder="Digite o código do pedido" value={formData.codigoPedido || ''} onChange={(e) => handleInputChange('codigoPedido', e.target.value)} />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700">{sale ? 'Salvar Alterações' : 'Salvar Venda'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SalesForm;